import { useSyncExternalStore } from 'react';
import { Company, PaginatedResponse, PricingTier } from '@/types';
import { api } from '@/services/api';
import { useStaffDataContext } from './StaffDataContext';

export type CompanyStatus = 'pending' | 'active' | 'on_hold' | 'rejected';

export interface CompanyListParams {
  page: number;
  pageSize: number;
  q?: string;
  status?: string;
  sortBy?: 'createdAt' | 'name';
  sortDir?: 'asc' | 'desc';
}

const buildQuery = (params: CompanyListParams) => ({
  page: params.page,
  pageSize: params.pageSize,
  q: params.q || undefined,
  status: params.status || undefined,
  sortBy: params.sortBy || undefined,
  sortDir: params.sortDir || undefined
});

export const useCompanies = (params: CompanyListParams): PaginatedResponse<Company> => {
  const ctx = useStaffDataContext();
  useSyncExternalStore(
    (cb) => ctx.subscribe('companies', cb),
    () => 0,
    () => 0
  );
  return ctx.read('companies', params, (p: CompanyListParams) =>
    api.listStaffCompanies(buildQuery(p))
  );
};

export interface CompanyMutations {
  create: (company: Company) => Promise<void>;
  update: (company: Partial<Company> & { id: string }) => Promise<void>;
  remove: (companyId: string) => Promise<void>;
  approve: (
    companyId: string,
    tier: PricingTier,
    paymentPref: 'invoice' | 'card',
    requirePo: boolean
  ) => Promise<void>;
  reject: (companyId: string) => Promise<void>;
  // Manual invalidation — for callers that mutate company state via APIs we
  // don't wrap (team-member ops, primary-member changes, etc.). Calling this
  // forces a re-fetch of the active/pending companies caches so derived fields
  // like teamCount or contactPerson stay in sync.
  invalidate: () => void;
}

export const useCompanyMutations = (): CompanyMutations => {
  const ctx = useStaffDataContext();
  const after = () => ctx.invalidate('companies');
  return {
    create: async (company) => {
      await api.createCompanyWithAuth(company);
      after();
    },
    update: async (company) => {
      await api.updateCompany(company);
      after();
    },
    remove: async (companyId) => {
      await api.deleteCompany(companyId);
      after();
    },
    approve: async (companyId, tier, paymentPref, requirePo) => {
      await api.updateCompany({
        id: companyId,
        status: 'active',
        tier,
        paymentPreference: paymentPref,
        requirePo
      });
      after();
    },
    reject: async (companyId) => {
      await api.updateCompany({ id: companyId, status: 'rejected' });
      after();
    },
    invalidate: after
  };
};
